<template>
    <div
        v-if="timelineSyncing"
        :style="{
            width: '95vw',
            height: '80vh',
            display: 'flex',
            'justify-content': 'center',
            'align-items': 'center',
            'font-size': '30px'
        }"
    >
        <b-spinner />&nbsp;&nbsp;Loading timeline...
    </div>
    <div
        v-else-if="timeline"
        class="timelineeditor"
    >
        <event-list-editor
            ref="eventList"
            style="z-index: 15;"
        />
        <notepad
            ref="notepad"
            style="z-index: 20;"
        />
        <span v-if="timeline != null">
            <div 
                v-show="shouldExpandPanel" 
                :class="`transition-100ms bring-to-front-mobile-portrait side-panel ${selectedObject != null ? 'editor' : 'creator'}`" 
                :style="shouldExpandPanel ? 'width: 30vw; min-width: min(375px, 100vw); max-width: min(1024px, 100vw);' : 'width: 0px;'"
            >
                <span v-show="selectedObject != null">
                    <!--Check if the selected object is a connection-->
                    <span v-if="selectedIsConnection">
                        <connection-editor 
                            ref="connectionEditor" 
                            :connection="selectedObject" 
                            @close="closeSidePanel"
                        />
                    </span>
                    <span v-else-if="selectedIsAnyNode">
                        <node-editor 
                            :key="`ned-${selectedObject.nodeid}`" 
                            ref="nodeEditor"
                            :node="selectedObject" 
                            @close="closeSidePanel" 
                            @scroll-to-node="scrollToNode"
                            @import-from-node="importFromNode"
                        />
                    </span>
                    <span v-else>
                        Unknown type of selected object
                    </span>
                </span>
                <span v-show="selectedObject == null && createType != -1">
                    <node-creator
                        :key="createKey"
                        :type="createType"
                        :timeline="timeline"
                        @close="closeSidePanel(true)"
                    />
                </span>
            </div>
        </span>
        <div v-if="timeline != null">
            <timeline-viewer 
                ref="timelineViewer" 
                :side-panel-open="shouldExpandPanel"
                :timeline="timeline" 
                class="transition-100ms" 
                :style="shouldExpandPanel ? 'margin-left: 30vw; width: calc(100% - 30vw);' : ''" 
                @close-side-panel="closeSidePanel" 
                @edit-selected="editSelected($event)"
            />
            <b-modal 
                ref="embedModal" 
                centered 
                size="lg" 
                title="Embed" 
                ok-only 
                ok-variant="secondary" 
                ok-title="Close"
            >
                <embed-modal
                    :key="`${timeline.id}-embed`"
                    :timelineids="[timeline.id]"
                />
            </b-modal>
            <b-modal 
                ref="shareModal" 
                centered 
                size="lg" 
                :title="$refs.shareLinkModal && $refs.shareLinkModal.showCreate ? 'Create Snapshot' : 'Share Timeline'" 
                ok-only 
                ok-variant="secondary" 
                ok-title="Close"
            >
                <share-link-modal
                    :key="`${timeline.id}-embed`"
                    ref="shareLinkModal"
                    :timelineids="[timeline.id]"
                    @snapshot-created="$refs.timelinePopup.show()"
                />
            </b-modal>
            <b-modal 
                ref="exportCSVModal" 
                centered 
                size="lg" 
                title="Download as CSV"
                ok-title="Download"
                @ok="() => { exportToCSV(); }"
            >
                <b-checkbox v-model="includeEventsInCSV">
                    Include Events
                </b-checkbox>
                <b-checkbox v-model="includeErasInCSV">
                    Include Eras
                </b-checkbox>
                <b-checkbox v-model="includeConnectionsInCSV">
                    Include Connections
                </b-checkbox>
            </b-modal>

            <timeline-survey-popup ref="timelinePopup" />

            <!-- TODO: componentize the following so it can be reused in TimelineViewer, TimelineEditor, NodeEditor, etc. -->
            <b-modal 
                ref="eventLimitModal" 
                centered 
                size="lg" 
                title="Event Limit Reached for non-Premium User" 
                ok-only 
                ok-variant="secondary"
                ok-title="Close"
            >
                <p style="text-align: center">
                    A non-premium user working in a non-upgraded project can only have <b>20 Events per timeline</b>.
                </p>
                <b-btn 
                    v-if="checkTimelineWrite() && !user.isSubscribed && !user.trialing && $store.state.project.project.subscriptionTier < 1"
                    v-b-tooltip.bottom="'Subscribing removes the limit'" 
                    style="margin: auto; margin-top: 10px; max-width: 90%" 
                    variant="cliovis-blue-light" 
                    size="sm" 
                    block
                    @click="$refs.subscriptionModal.show()"
                >
                    <i class="fas fa-plus" /> Increase Limit
                </b-btn>
            </b-modal>
            <b-modal
                ref="subscriptionModal"
                centered
                title="Subscribe"
                ok-only
                ok-variant="secondary"
                ok-title="Close"
                size="wide-3"
                no-close-on-backdrop
                no-close-on-esc
            >
                <subscription-panel />
                <div
                    v-if="checkAdmin() && !$store.state.project.project.upgradedOverride"
                    style="margin-top: 15px; text-align: center; align-items: center"
                >
                    <span><i
                        class="fas fa-certificate"
                        style="font-size: 24px; margin-right: 6px; color: #247a59;"
                    />Or, would you like to remove the limit for yourself and all non-premium users of this Project?</span>
                    <b-btn
                        v-b-tooltip.ds550="'Upgrade this Project if you want to collaborate with non-premium users & pay for their access to this Project'"
                        block
                        pill
                        variant="outline-dark"
                        style="max-width: 300px; margin: auto; margin-top: 10px"
                        @click="$refs.upgradeProjectModal.show()"
                    >
                        <i class="fas fa-file-invoice-dollar" /> Upgrade Project
                    </b-btn>
                </div>
            </b-modal>
            <b-modal
                ref="upgradeProjectModal"
                size="wide-3"
                centered
                title="Upgrade Project - Manage Payment Plan"
                hide-footer
                no-close-on-backdrop
                no-close-on-esc
            >
                <subscription-panel
                    project
                    :pid="$store.state.project.project.id"
                    :on-subscribe="() => { this.$router.push(`/app/reconnect/${$store.state.project.project.id}/${timeline.id}`); }"
                />
            </b-modal>
            <b-modal
                id="genealogyImporterModal"
                ref="genealogyImporterModal"
                size="lg"
                title="Genealogy Importer"
                centered
                dialog-class="gidialog"
            >
                <genealogy-importer
                    ref="genealogyImporter"
                    :timelineid="timeline.id"
                    :event-limit-reached="eventLimitReached"
                    @show-event-limit="$refs.eventLimitModal.show()"
                />

                <template #modal-footer="{ ok }">
                    <b-button
                        size="md"
                        variant="secondary"
                        style="margin-right: auto;"
                        @click="ok()"
                    >
                        Close
                    </b-button>
                </template>
            </b-modal>
        </div>
        <b-modal
            ref="quizGeneratorModal"
            size="lg"
            title="Quiz Generator"
            centered
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
        >
            <proto-quiz-generator
                :events="activeTimelineNodes"
                :connections="activeTimelineConnections"
            />
        </b-modal>
        <b-modal
            ref="documentImportModal"
            size="lg"
            title="Document Import"
            centered
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
        >
            <document-import :timelineid="timeline.id" />
        </b-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import EmbedModal from '@/components/embed/EmbedModal.vue';
import ShareLinkModal from '@/components/embed/ShareLinkModal.vue';
import EventListEditor from '@/components/event_list/EventListEditor.vue';
import GenealogyImporter from '@/components/genealogy/GenealogyImporter.vue';
import Notepad from '@/components/Notepad';
import SubscriptionPanel from '@/components/payment/SubscriptionPanel.vue';
import KeyListener from '@/KeyListener';
import DateOptions from '@/models/DateOptions';
import { PacketType } from '@/models/Packet';
import ProjectServer from '@/models/ProjectServer';
// eslint-disable-next-line no-unused-vars
import Timeline from '@/models/Timeline';
import Node, { NodeType } from '@/models/Node';
import Mutations from '@/store/mutations';
import FileUtil from '@/util/FileUtil';
import TimelineViewer from '@/views/timelineeditor/TimelineViewer.vue';
import NodeEditor from '@/views/timelineeditor/side-panel/NodeEditor.vue';
import NodeCreator from '@/views/timelineeditor/side-panel/NodeCreator.vue';
import ConnectionEditor from '@/views/timelineeditor/side-panel/ConnectionEditor.vue';
import { IsEmbedded } from '@/constants/environment';
import TimelineSurveyPopup from '@/components/user_questions/TimelineSurveyPopup.vue';
import ProtoQuizGenerator from './timelineeditor/ProtoQuizGenerator.vue';
import DocumentImport from './timelineeditor/DocumentImport.vue';

const CreateTypes = {
    None: -1,
    Node: 0,
    Era: 1
};

export default Vue.extend({
    refs: ['embedModal', 'shareLinkModal'],
    components: 
    {
        ProtoQuizGenerator,
        DocumentImport,
        TimelineSurveyPopup,
        ShareLinkModal,
        TimelineViewer,
        NodeEditor,
        NodeCreator,
        ConnectionEditor,
        Notepad,
        EventListEditor,
        EmbedModal,
        SubscriptionPanel,
        GenealogyImporter
    },
    data()
    {
        return {
            oldId: -1,
            createKey: 0,
            createType: CreateTypes.None,
            editSelectedAfterCreate: false,
            includeEventsInCSV: true,
            includeErasInCSV: true,
            includeConnectionsInCSV: true,
        };
    },
    computed:
    {
        ...mapGetters([
            'checkTimelineShare',
            'checkTimelineWrite',
            'checkAdmin',
            'nodes',
            'timelineFormMode',
            'activeTimelineNodes',
            'activeTimelineConnections',
            'accessedTimelines',
            'timelineSyncing',
        ]),
        activeTimeline()
        {
            return this.$store.state.project.activeTimeline;
        },
        canShareTimeline()
        {
            return this.checkTimelineShare(this.activeTimeline.id);
        },
        shouldExpandPanel()
        {
            return !(this.presentationMode) 
                && ((this.selectedObject == null && this.createType != -1) || (this.selectedObject != null && !(this.timelineFormMode)));
        },
        timeline()
        {
            return this.$store.state.project.activeTimeline;
        },
        timelineName()
        {
            return this.timeline != null ? this.timeline.name : '[null]';
        },
        selectedObject()
        {
            return this.$store.state.project.selected;
        },
        presentationMode()
        {
            return this.$store.state.project.presentationMode;
        },
        selectedIsConnection()
        {
            return this.selectedObject && this.selectedObject.nodeid1 != undefined;
        },
        selectedIsAnyNode()
        {
            return this.selectedObject && this.selectedObject.isEra != undefined
                && this.selectedObject.nodeid1 == undefined;
        },
        selectedIsEra()
        {
            return this.selectedIsAnyNode && this.selectedObject.isEra();
        },
        eventLimitReached()
        {
            return !IsEmbedded
                && this.user
                && this.eventCount >= 20
                && !(this.user.isSubscribed)
                && !(this.user.trialing)
                && !(this.$store.state.project.project.upgradedOverride) 
                && (this.$store.state.project.project.subscriptionTier < 1);
        },
        eventCount()
        {
            let numEvents = 0;
            for (const n of this.activeTimelineNodes)
            {
                if (!(n.isEra()))
                {
                    ++numEvents;
                }
            }
            return numEvents;
        }
    },
    watch: 
    {
        "$route"(val, old)
        {
            this.setupTimeline();
        },
        selectedObject(val)
        {
            if (val && this.editSelectedAfterCreate)
            {
                this.editSelectedAfterCreate = false;
                this.editSelected();
            }
        }
    },
    mounted()
    {
        console.log('Mounted timeline editor');
        this.setupTimeline();
        KeyListener.addKeyHandler(this);
    },
    beforeDestroy()
    {
        KeyListener.removeKeyHandler(this);
    },
    methods:
    {
        editSelected(editorType)
        {
            this.$nextTick(() =>
            {
                if (editorType === 'connection')
                {
                    this.$refs.connectionEditor.edit();
                }
                else if (this.selectedObject)
                {
                    this.$refs.nodeEditor.edit();
                }
            });
        },
        closeSidePanel(closeCreatePanel = false)
        {
            if (this.selectedIsAnyNode) 
            {
                this.$refs.nodeEditor.onFinishedEditing();
            }
            
            this.$store.commit(Mutations.setSelected, null);
            this.$root.$emit('bv::hide::tooltip');

            const last = this.$store.state.project.lastSelected;
            if (last != null && last.component != null && last.component.$el != null)
            {
                if (last.isEra())
                {
                    const el = document.getElementById(`eraBar${last.nodeid}`);
                    if (el) el.focus();
                }
                else
                {
                    const el = document.getElementById(`nodeCircle${last.nodeid}`);
                    if (el) el.focus();
                }
            }

            if (closeCreatePanel)
            {
                this.createType = CreateTypes.None;
            }
        },
        closeGenealogyImporter()
        {
            console.log('wtf');
            this.$refs.genealogyImporterModal.hide();
        },
        scrollToNode()
        {
            this.$refs.timelineViewer.scrollToNode(this.$store.state.project.selected);
        },
        async importFromNode()
        {
            if (this.$refs.genealogyImporterModal != null)
            {
                this.$refs.genealogyImporterModal.show();
            }
            await this.$nextTick();
            if (this.$refs.genealogyImporter != null)
            {
                this.$refs.genealogyImporter.setSelectedPersonFromExistingNode(this.$store.state.project.selected);
            }
        },
        createNode(type = NodeType.TimelineEvent)
        {
            // first check the free tier event limit
            if (this.eventLimitReached)
            {
                this.$refs.eventLimitModal.show();
                return;
            }

            if (this.presentationMode)
            {
                this.$store.commit(Mutations.setPresentationMode, false);
            }
            let n = new Node;
            n.type = type;
            n.name = `${this.user.uiName}'s new ${(type === NodeType.TimelineEvent) ? 'event' : 'era'}`;
            let defaultYear = 
                (this.$refs.timelineViewer != null && this.$refs.timelineViewer.centerYear != null) 
                    ? this.$refs.timelineViewer.centerYear() : null;
            if (isNaN(defaultYear))
            {
                n.dateStart = (this.nodes.size > 0) ? (new Date(this.nodes.values().next().value.dateStart.valueOf())) : (new Date());
            }
            else
            {
                n.dateStart = new Date();
                n.dateStart.setUTCFullYear(defaultYear);
            }
            n.dateEnd = new Date((type === NodeType.TimelineEvent) ? n.dateStart.valueOf() : (n.dateStart.valueOf() + 315569520000));
            n.dateOptions = new DateOptions(0);
            let cb = ProjectServer.instance.registerCallback(PacketType.NodeCreate, (data) =>
            {
                if (data.name.substring(1) === n.name)
                {
                    ProjectServer.instance.removeCallback(PacketType.NodeCreate, cb);
                    this.$nextTick(() =>
                    {
                        this.editSelected();
                    });
                }
            });
            this.editSelectedAfterCreate = true;
            Node.create(n, this.timeline.id);
        },
        getEarlyNavItems()
        {
            return [
                {
                    space: "5px"
                },
                {
                    text: 'Timeline List',
                    iconClass: "fas fa-th",
                    click: () =>
                    {
                        this.$router.push('/app/project/timelines/');
                    },
                },
            ];
        },
        keyHandler(event, isTextBoxFocused)
        {
            if (!isTextBoxFocused)
            {
                if (KeyListener.isNDown())
                {
                    if (KeyListener.isShiftDown())
                    {
                        this.createNode(NodeType.TimelineEra);
                    }
                    else
                    {
                        this.createNode(NodeType.TimelineEvent);
                    }
                }
                else if (KeyListener.isCDown())
                {
                    this.$store.commit(Mutations.setSelected, null);

                    const last = this.$store.state.project.lastSelected;
                    if (last != null && last.component != null && last.component.$el != null)
                    {
                        if (last.isEra())
                        {
                            const el = document.getElementById(`eraBar${last.nodeid}`);
                            if (el) el.focus();
                        }
                        else
                        {
                            const el = document.getElementById(`nodeCircle${last.nodeid}`);
                            if (el) el.focus();
                        }
                    }

                    this.createType = -1;
                }
            }
        },
        getNavItems()
        {
            if (this.checkTimelineWrite())
            {
                return [
                    {
                        text: "Create",
                        children: [
                            {
                                text: 'Create Event <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(N)</span>',
                                iconClass: "fas fa-plus-circle",
                                click: () =>
                                {
                                    this.createNode(NodeType.TimelineEvent);
                                },
                            },
                            {
                                text: 'Create Era <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(shift+N)</span>',
                                iconClass: "fas fa-plus-circle",
                                click: () =>
                                {
                                    this.createNode(NodeType.TimelineEra);
                                },
                            }
                        ]
                    },
                    {
                        text: "Export",
                        children: [
                            {
                                text: 'Download as DOCX',
                                iconClass: "fas fa-file-export",
                                click: () =>
                                {
                                    if (this.canShareTimeline)
                                    {
                                        this.timeline.exportToDocx();

                                        this.$bvToast.toast('Please check your Downloads folder for the exported file.',
                                            {
                                                title: 'Exporting...',
                                                variant: 'default',
                                                autoHideDelay: 5000
                                            });
                                    }
                                    else
                                    {
                                        this.$bvToast.toast('You do not have permission to share data from this timeline.',
                                            {
                                                title: 'Error',
                                                variant: 'danger',
                                                autoHideDelay: 5000
                                            });
                                    }
                                },
                            },
                            {
                                text: 'Download as CSV',
                                iconClass: "fas fa-file-export",
                                click: () =>
                                {
                                    if (this.canShareTimeline)
                                    {
                                        this.$refs.exportCSVModal.show();
                                    }
                                    else
                                    {
                                        this.$bvToast.toast('You do not have permission to share data from this timeline.',
                                            {
                                                title: 'Error',
                                                variant: 'danger',
                                                autoHideDelay: 5000
                                            });
                                    }
                                },
                            },
                            /*{
                                text: 'Embed Timeline',
                                iconClass: "fas fa-globe",
                                click: () =>
                                {
                                    this.$refs.embedModal.show();
                                },
                            },*/
                            {
                                text: 'Share Timeline',
                                iconClass: "fas fa-share",
                                click: () =>
                                {
                                    this.$refs.shareModal.show();
                                },
                            },
                            {
                                text: '.'
                            },
                        ]
                    },
                    {
                        text: "Tools",
                        children: [
                            {
                                text: 'Open Event List',
                                iconClass: "fas fa-bars",
                                click: () =>
                                {
                                    this.$refs.eventList.show();
                                },
                            },
                            {
                                text: '.'
                            },
                            {
                                text: (this.presentationMode ? 'End Presentation' : 'Start Presentation'),
                                iconClass: "fas fa-chalkboard-teacher",
                                click: () =>
                                {
                                    this.$store.commit(Mutations.setPresentationMode, !(this.presentationMode));
                                    if (this.presentationMode)
                                    {
                                        let ordering = this.timeline.getEventOrdering();
                                        if (ordering != null && ordering.length > 0)
                                        {
                                            let firstNode = this.nodes.get(ordering[0]);
                                            if (firstNode != null)
                                            {
                                                firstNode.component.onNodeClick();
                                            }
                                        }
                                    }
                                    else
                                    {
                                        this.$store.commit(Mutations.setSelected, null);
                                    }
                                },
                            },
                            {
                                text: '.'
                            },
                            {
                                text: 'Open Notepad',
                                iconClass: "fas fa-clipboard",
                                click: () =>
                                {
                                    this.$refs.notepad.show();
                                },
                            },
                            {
                                text: '.'
                            },
                            {
                                text:
                                    (this.timelineFormMode)
                                        ? 'Visual Timeline View <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(V)</span>'
                                        : 'Simplified Form View <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(V)</span>',
                                iconClass: (this.timelineFormMode) ? "fas fa-sitemap" : "fas fa-th-list",
                                click: () =>
                                {
                                    this.$store.commit(Mutations.setTimelineFormMode, !(this.timelineFormMode));
                                },
                            },
                            {
                                text: '.'
                            },
                            {
                                text: 'Search Timeline <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(S)</span>',
                                iconClass: "fas fa-search",
                                click: () =>
                                {
                                    if (this.$refs.timelineViewer != null)
                                    {
                                        this.$refs.timelineViewer.openQuicksearch();
                                    }
                                },
                            },
                            {
                                text: '.'
                            },
                            {
                                text: 'Genealogy Importer',
                                iconClass: "fas fa-people-arrows",
                                click: () =>
                                {
                                    if (this.$refs.genealogyImporterModal != null)
                                    {
                                        this.$refs.genealogyImporterModal.show();
                                    }
                                },
                            },
                            {
                                text: 'Generate Quiz (Preview)',
                                iconClass: 'fas fa-question',
                                click: ()=>
                                {
                                    this.$refs.quizGeneratorModal.show();
                                },
                                visible: ()=>
                                {
                                    // if the local user is a global admin
                                    return this.user && this.user.isAdmin;
                                }
                            },
                            {
                                text: 'Import Document (Preview)',
                                iconClass: 'fas fa-file-import',
                                click: ()=>
                                {
                                    this.$refs.documentImportModal.show();
                                },
                                visible: ()=>
                                {
                                    // if the local user is a global admin
                                    return this.user && this.user.isAdmin;
                                }
                            },
                        ]
                    },
                    {
                        text: this.presentationMode ? '<span style="border: 1.33px solid #343B3F; border-radius: 20px; padding: 5px 7px 5px 7px"><i class="fas fa-chalkboard-teacher"></i>End Presentation</span>' : '',
                        iconClass: "fas fa-chalkboard-teacher",
                        click: () =>
                        {
                            this.$store.commit(Mutations.setPresentationMode, false);
                            this.$store.commit(Mutations.setSelected, null);
                        }
                    },
                ];
            }
            else
            {
                return [
                    {
                        text: "Export",
                        hiddenInEmbed: true,
                        children: [
                            {
                                text: 'Download as DOCX',
                                iconClass: "fas fa-file-export",
                                click: () =>
                                {
                                    if (this.canShareTimeline)
                                    {
                                        this.timeline.exportToDocx();

                                        this.$bvToast.toast('Please check your Downloads folder for the exported file.',
                                            {
                                                title: 'Exporting...',
                                                variant: 'default',
                                                autoHideDelay: 5000
                                            });
                                    }
                                    else
                                    {
                                        this.$bvToast.toast('You do not have permission to share data from this timeline.',
                                            {
                                                title: 'Error',
                                                variant: 'danger',
                                                autoHideDelay: 5000
                                            });
                                    }
                                },
                            },
                            {
                                text: 'Download as CSV',
                                iconClass: "fas fa-file-export",
                                click: () =>
                                {
                                    if (this.canShareTimeline)
                                    {
                                        this.$refs.exportCSVModal.show();
                                    }
                                    else
                                    {
                                        this.$bvToast.toast('You do not have permission to share data from this timeline.',
                                            {
                                                title: 'Error',
                                                variant: 'danger',
                                                autoHideDelay: 5000
                                            });
                                    }
                                },
                            },
                            /*{
                                text: 'Embed Timeline',
                                iconClass: "fas fa-globe",
                                click: () =>
                                {
                                    this.$refs.embedModal.show();
                                },
                            },*/
                            {
                                text: 'Share Timeline',
                                iconClass: "fas fa-share",
                                click: () =>
                                {
                                    this.$refs.shareModal.show();
                                },
                            },
                        ]
                    },
                    {
                        text: "Tools",
                        hiddenInEmbed: true,
                        children: [
                            {
                                text: (this.presentationMode ? 'End Presentation' : 'Start Presentation'),
                                iconClass: "fas fa-chalkboard-teacher",
                                click: () =>
                                {
                                    this.$store.commit(Mutations.setPresentationMode, !(this.presentationMode));
                                    if (this.presentationMode)
                                    {
                                        let ordering = this.timeline.getEventOrdering();
                                        if (ordering != null && ordering.length > 0)
                                        {
                                            let firstNode = this.nodes.get(ordering[0]);
                                            if (firstNode != null)
                                            {
                                                firstNode.component.onNodeClick();
                                            }
                                        }
                                    }
                                    else
                                    {
                                        this.$store.commit(Mutations.setSelected, null);
                                    }
                                },
                            },
                            {
                                text: '.'
                            },
                            {
                                text:
                                    (this.timelineFormMode)
                                        ? 'Visual Timeline View <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(V)</span>'
                                        : 'Simplified Form View <span style="color: #6f7989; font-size: 80%; margin-left: 10px;">(V)</span>',
                                iconClass: "fas fa-th",
                                click: () =>
                                {
                                    this.$store.commit(Mutations.setTimelineFormMode, !(this.timelineFormMode));
                                },
                            }
                        ]
                    },
                    {
                        text: !this.presentationMode ? 'Start Presentation' : '',
                        onlyShowInEmbed: true,
                        iconClass: "fas fa-chalkboard-teacher",
                        click: (this.presentationMode) ? undefined : () =>
                        { 
                            this.$store.commit(Mutations.setPresentationMode, true);
                            if (this.presentationMode)
                            {
                                let ordering = this.timeline.getEventOrdering();
                                if (ordering != null && ordering.length > 0)
                                {
                                    let firstNode = this.nodes.get(ordering[0]);
                                    if (firstNode != null)
                                    {
                                        firstNode.component.onNodeClick();
                                    }
                                }
                            }
                            else
                            {
                                this.$store.commit(Mutations.setSelected, null);
                            }
                        }
                    },
                    {
                        text: this.presentationMode ? 'End Presentation' : '',
                        iconClass: "fas fa-chalkboard-teacher",
                        click: !(this.presentationMode) ? undefined : () =>
                        { 
                            this.$store.commit(Mutations.setPresentationMode, false);
                            this.$store.commit(Mutations.setSelected, null);
                        }
                    }
                ];
            }
        },
        setupTimeline()
        {
            if (this.$route.params.id != this.oldId)
            {
                console.log(`Reading timeline ${this.$route.params.id}`);
                let val = parseInt(this.$route.params.id);
                if (val == undefined)
                {
                    this.$router.push("/app/project/");
                    return;
                }

                try
                {
                    let tgTimeline = this.$store.state.project.timelines.get(val);
                    console.log("TIMELINE BELOW");
                    console.log(tgTimeline);
                    console.log("TIMELINE ABOVE");
                    
                    if (!tgTimeline)
                    {
                        alert("Timeline does not exist, returning to timeline list");
                        this.$router.push("/app/project/timelines/list");
                        return;
                    }

                    this.oldId = val;
                    if (this.accessedTimelines == null || !(this.accessedTimelines.length)
                        || !(this.accessedTimelines.includes(val)))
                    {
                        ProjectServer.instance.sendData(PacketType.TimelineSyncRequest, { timelineid: val });
                    }
                    this.$store.commit(Mutations.setActiveTimeline, { 'activeTimeline': tgTimeline });
                }
                catch(e)
                {
                    console.error(e);
                }
            }
        },
        exportToCSV()
        {
            let csv = '"name","type","description","references","marked important","start date","end date","image URL","image caption","audio URL","category"';
            if (this.includeEventsInCSV || this.includeErasInCSV)
            {
                for (const n of this.activeTimelineNodes)
                {
                    if (!(this.includeEventsInCSV) && n.isEvent())
                    {
                        continue;
                    }
                    if (!(this.includeErasInCSV) && n.isEra())
                    {
                        continue;
                    }
                    csv = n.addToCSV(csv);
                }
            }
            if (this.includeConnectionsInCSV)
            {
                for (const c of this.activeTimelineConnections)
                {
                    csv = c.addToCSV(csv, this.nodes);
                }
            }
            FileUtil.exportToFile(this.activeTimeline.name, '.csv', csv);

            this.$bvToast.toast('Please check your Downloads folder for the exported file.',
                {
                    title: 'Exporting...',
                    variant: 'default',
                    autoHideDelay: 5000
                });
        }
    },
    beforeRouteLeave(to, from, next)
    {
        KeyListener.removeKeyHandler(this);
        this.$store.commit(Mutations.setActiveTimeline, { 'activeTimeline': null });
        next();
    }
});
</script>

<style lang="scss" scoped>
.transition-100ms
{
    transition-duration: 100ms;
}

.side-panel
{
    box-shadow: 0px 0px 8px rgba(0,0,0,0.2);
    position: fixed;
    z-index: 200;
    height: calc(100% - 45px);
    overflow-y: auto;
    background-color: #F6F0EE;
    margin-top: -20px;

    @media (max-width: 600px)
    {
        @media only screen and (orientation:portrait){
            margin-top: -60px;
            height: calc(100% - 5px);
        }
    }
}

.editor
{
    height: calc(100% - 45px);

    @media (max-width: 600px)
    {
        @media only screen and (orientation:portrait){
            height: calc(100% - 5px);
        }
    }
}

.creator
{
    height: calc(100% - 45px);
    padding-bottom: 15px;

    @media (max-width: 600px)
    {
        @media only screen and (orientation:portrait){
            height: calc(100% - 5px);
        }
    }
}

.expand-horizontal
{
    width: 100%;
}

.center-text
{
    text-align: center;
}

/deep/ .gidialog
{
    margin-left: 378px !important; // keep side panel visible
}
</style>